import * as RadixDialog from '@radix-ui/react-dialog';
import { t } from '../i18n/index.js';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { IconX } from './Icon.js';

/**
 * Dialog.
 *
 * Radix supplies the parts that are genuinely hard: focus moves into the panel
 * on open and returns to the trigger on close, Tab is trapped inside, Escape
 * closes, the rest of the page is hidden from screen readers via `aria-modal`,
 * and background scroll is locked without the page jumping as the scrollbar
 * disappears.
 *
 * ── A DESCRIPTION IS REQUIRED, NOT OPTIONAL ───────────────────────────────
 *
 * `description` is a required prop. Radix warns in the console when a dialog
 * has no description, and the warning is right: a screen reader announces the
 * title and then nothing, so "Reverse this voucher" arrives with no indication
 * that the original stays in the books.
 *
 * In this product that matters more than usual — every dialog here either posts
 * to the ledger or changes the chart of accounts. Making it required means the
 * consequence gets stated. `descriptionHidden` keeps it for assistive tech when
 * the visual design does not want it.
 */

/*
  `DialogTrigger` and `DialogClose` are deliberately NOT re-exported here.

  Both dialogs in this console are opened from state and closed by a footer
  button, so the Radix trigger and close primitives had no callers and the
  dead-code guard refused them. Re-export them when something needs one —
  they are one line each, and an unused re-export is a component somebody
  reasonably assumes is the house pattern.
*/
export const Dialog = RadixDialog.Root;

export type DialogWidth = 'sm' | 'md' | 'lg';

const WIDTH: Record<DialogWidth, string> = {
  sm: 'w-(--dialog-width-sm)',
  md: 'w-(--dialog-width-md)',
  lg: 'w-(--dialog-width-lg)',
};

export function DialogContent({
  title,
  description,
  descriptionHidden = false,
  width = 'sm',
  footer,
  className,
  children,
}: {
  title: ReactNode;
  /** What this dialog will do. Required — see the note above. */
  description: ReactNode;
  descriptionHidden?: boolean;
  width?: DialogWidth;
  footer?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        className={cn(
          'fixed inset-0 z-(--p-z-overlay) bg-black/40 backdrop-blur-[1px]',
          'backdrop-motion',
        )}
      />
      <RadixDialog.Content
        className={cn(
          // `.cosmos-v2` because this renders into document.body, outside the
          // app tree, and has to carry the reset and tokens with it.
          'cosmos-v2 dialog-motion',
          'fixed left-1/2 top-1/2 z-(--p-z-modal) -translate-x-1/2 -translate-y-1/2',
          'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-4rem)] overflow-y-auto',
          'rounded-(--dialog-radius) border border-line bg-overlay shadow-(--shadow-modal)',
          WIDTH[width],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 p-(--dialog-padding) pb-0">
          <div className="min-w-0 flex flex-col gap-1">
            <RadixDialog.Title className="text-xl font-semibold tracking-tight text-primary">
              {title}
            </RadixDialog.Title>
            <RadixDialog.Description
              className={cn(
                'text-md leading-snug text-tertiary',
                descriptionHidden && 'sr-only',
              )}
            >
              {description}
            </RadixDialog.Description>
          </div>
          <RadixDialog.Close asChild>
            <Button variant="ghost" size="sm" aria-label={t('a11y.close')}>
              <IconX />
            </Button>
          </RadixDialog.Close>
        </div>

        {children && <div className="p-(--dialog-padding) pt-4">{children}</div>}

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-subtle bg-sunken px-(--dialog-padding) py-3">
            {footer}
          </div>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
